/**
 * ModuleGuideTour
 * Komponent för modulspecifika guider (Nivå 2).
 * Monteras i en modulsida och hanterar både auto-start (första besöket)
 * och manuell start via startTour()-funktionen som exponeras via ref.
 */

import { useImperativeHandle, forwardRef, useCallback } from 'react';
import Joyride, { type CallBackProps } from 'react-joyride';
import { STATUS } from 'react-joyride';
import { useGuideTour } from '@/hooks/useGuideTour';
import { useTranslation } from '@/store/languageStore';

export interface ModuleGuideTourHandle {
  startTour: () => void;
  restartTour: () => void;
}

interface ModuleGuideTourProps {
  tourKey: string;
  /** Auto-starta turen om ej sedd (default: true) */
  autoStart?: boolean;
  /** Callback när turen avslutas (finished eller skipped) */
  onFinish?: () => void;
}

const ModuleGuideTour = forwardRef<ModuleGuideTourHandle, ModuleGuideTourProps>(
  ({ tourKey, autoStart = true, onFinish }, ref) => {
    const { t } = useTranslation();
    const {
      run,
      steps,
      joyrideKey,
      isReady,
      startTour,
      restartTour,
      handleJoyrideCallback,
    } = useGuideTour(tourKey, autoStart);

    useImperativeHandle(ref, () => ({ startTour, restartTour }), [startTour, restartTour]);

    const combinedCallback = useCallback(
      (data: CallBackProps) => {
        handleJoyrideCallback(data);
        if (
          data.status === STATUS.FINISHED ||
          data.status === STATUS.SKIPPED
        ) {
          onFinish?.();
        }
      },
      [handleJoyrideCallback, onFinish]
    );

    // Rendera Joyride BARA när turen körs – annars fastnar overlay i DOM
    if (!isReady || steps.length === 0 || !run) return null;

    return (
      <Joyride
        key={joyrideKey}
        run={run}
        steps={steps}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        spotlightClicks
        disableScrollParentFix
        callback={combinedCallback}
        locale={{
          back: t('guide.back'),
          close: t('guide.close'),
          last: t('guide.finish'),
          next: t('guide.next'),
          skip: t('guide.skip'),
        }}
        styles={{
          options: {
            primaryColor: 'var(--bgPrimary, #2563eb)',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: '0.75rem',
            fontSize: '0.95rem',
            maxWidth: '380px',
          },
          tooltipTitle: {
            fontSize: '1.05rem',
            fontWeight: 600,
          },
          buttonNext: {
            borderRadius: '0.5rem',
            padding: '0.5rem 1.25rem',
          },
          buttonBack: {
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            marginRight: '0.5rem',
          },
          buttonSkip: {
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
          },
        }}
      />
    );
  }
);

ModuleGuideTour.displayName = 'ModuleGuideTour';

export default ModuleGuideTour;
